import { NavLink } from 'react-router-dom';
import {
  FileText,
  Settings,
  Users,
  Building2,
  PanelLeftClose,
  PanelLeftOpen,
  Package,
  Palette,
  Shield,

} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useAuthStore } from '../../stores/auth.store';
import { contractLensService } from '../../services/contractlens.service';

/**
 * Primary navigation.
 *
 * Design notes (see docs/DESIGN-SYSTEM.md):
 *  - The rail is ink, not a saturated colour, so it recedes and the content leads.
 *  - The active item is marked with an accent bar plus a background change, so the
 *    state does not rely on colour alone.
 *  - Collapsed state persists, because a preference that resets every navigation is
 *    worse than no preference at all.
 *  - When collapsed, labels become native tooltips so the icons stay identifiable.
 */
interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  productCode?: string;
}

const ICON_SIZE = 18;

const productIcons: Record<string, React.ReactNode> = {
  CONTRACTLENS: <FileText size={ICON_SIZE} />,
};

const globalAdminItems: NavItem[] = [
  { label: 'Kunder', path: '/admin/customers', icon: <Building2 size={ICON_SIZE} /> },
  { label: 'Brukere', path: '/admin/users', icon: <Users size={ICON_SIZE} /> },
  { label: 'Global Admins', path: '/admin/global-admins', icon: <Shield size={ICON_SIZE} /> },
  { label: 'ContractLens', path: '/admin/contractlens', icon: <Palette size={ICON_SIZE} /> },
  { label: 'Innstillinger', path: '/admin/settings', icon: <Settings size={ICON_SIZE} /> },
];

const programAdminItems: NavItem[] = [
  { label: 'Brukere', path: '/admin/users', icon: <Users size={ICON_SIZE} /> },
];


/** Remembers the rail width across sessions. */
const COLLAPSE_KEY = 'lynki:sidebar-collapsed';

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === 'true'
  );
  const [customerLogoUrl, setCustomerLogoUrl] = useState<string | null>(null);
  const { user, accessibleProducts, isCustomerAdmin } = useAuthStore();

  const isGlobalAdmin = user?.roles?.includes('GlobalAdmin');
  const isAdmin =
    isGlobalAdmin ||
    isCustomerAdmin ||
    user?.roles?.includes('CustomerAdmin') ||
    user?.roles?.includes('ProgramAdmin');

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const fetchCustomerLogo = async () => {
      try {
        const settings = await contractLensService.getCustomerSettings();
        if (settings.hasLogo && settings.logoUrl) {
          setCustomerLogoUrl(settings.logoUrl);
        }
      } catch {
        // Fall back to the wordmark. A missing logo is not worth an error state.
      }
    };

    if (user) {
      void fetchCustomerLogo();
    }
  }, [user]);

  const productNavItems = useMemo(() => {
    const dynamic = accessibleProducts
      .filter((p) => p.url && p.url !== '/')
      .map((product) => ({
        label: product.name,
        path: product.url!,
        icon: productIcons[product.code?.toUpperCase() || ''] || <Package size={ICON_SIZE} />,
        productCode: product.code,
      }));
    return dynamic;
  }, [accessibleProducts]);

  const renderSection = (title: string, items: NavItem[]) => (
    <div>
      {!collapsed && (
        <div className="px-5 pb-1.5">
          {/*
            ink-300 rather than ink-500: this label sits on the near-black rail, where
            the darker token only reaches 3.46:1. ink-300 gives 9.7:1 and clears WCAG AA
            for small text. Verified by scripts/verify-contrast.mjs.
          */}
          <span className="text-[10px] font-semibold text-ink-300 uppercase tracking-[0.12em]">
            {title}
          </span>
        </div>
      )}
      <ul className="space-y-px px-2">
        {items.map((item) => (
          <li key={item.path}>
            <NavLink
              to={item.path}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                [
                  'group relative flex items-center gap-3 rounded-[--radius-control]',
                  'px-3 py-2 transition-colors duration-150',
                  collapsed ? 'justify-center' : '',
                  isActive
                    ? 'bg-ink-800 text-white'
                    : 'text-ink-300 hover:bg-ink-800/60 hover:text-white',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {/* Accent bar: marks the active item without relying on colour alone. */}
                  <span
                    aria-hidden
                    className={[
                      'absolute -left-2 top-1/2 -translate-y-1/2 w-1 rounded-r-full',
                      'transition-all duration-150',
                      isActive ? 'h-6 bg-accent-400' : 'h-0 bg-transparent',
                    ].join(' ')}
                  />
                  <span className="shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <span className="text-[13px] font-medium truncate">{item.label}</span>
                  )}
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <aside
      aria-label="Hovednavigasjon"
      className={[
        'bg-ink-950 text-white flex flex-col shrink-0',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[68px]' : 'w-60',
      ].join(' ')}
    >
      {/* Brand */}
      <div
        className={[
          'h-14 flex items-center border-b border-white/[0.06]',
          collapsed ? 'justify-center px-2' : 'justify-between pl-5 pr-3',
        ].join(' ')}
      >
        {!collapsed &&
          (customerLogoUrl ? (
            <img
              src={customerLogoUrl}
              alt=""
              className="h-7 max-w-[130px] object-contain"
              onError={() => setCustomerLogoUrl(null)}
            />
          ) : (
            <span className="text-[15px] font-semibold tracking-tight text-white">Lynki</span>
          ))}
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? 'Utvid menyen' : 'Skjul menyen'}
          aria-expanded={!collapsed}
          className="p-1.5 rounded-[--radius-control] text-ink-400 hover:text-white hover:bg-white/[0.06] transition-colors"
        >
          {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 space-y-5 overflow-y-auto">
        {productNavItems.length > 0 && renderSection('Produkter', productNavItems)}

        {isAdmin &&
          renderSection(
            isGlobalAdmin ? 'Administrasjon' : 'Programadmin',
            isGlobalAdmin ? globalAdminItems : programAdminItems
          )}
      </nav>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-white/[0.06]">
        {!collapsed && (
          <p className="text-[10px] text-ink-600">&copy; {new Date().getFullYear()} Lynki AS</p>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
